import { Link, useParams } from "react-router-dom";
import { ArrowRight, AlertCircle } from "lucide-react";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal } from "../components/Reveal";
import { PagePhoto } from "../components/PagePhoto";
import { pagePhotos } from "../content/photos";
import { CTASection } from "../components/CTASection";
import NotFound from "./NotFound";
import { trackEvent } from "../lib/analytics";
import { useLang, useLp, localeUrl } from "../i18n/lang";
import { useCopy } from "../i18n/copy";
import { useServices, useProblems, useGuides } from "../i18n/content";

export default function ProblemPage() {
  const { slug } = useParams();
  const problems = useProblems();
  const services = useServices();
  const guides = useGuides();
  const c = useCopy("problemPage");
  const cr = useCopy("crumbs");
  const cm = useCopy("common");
  const lp = useLp();
  const lang = useLang();
  const problem = problems.find((p) => p.slug === slug);
  if (!problem) return <NotFound />;

  const crumbs = [
    { name: cr.home, path: lp("/") },
    { name: cr.problems, path: lp("/problemas/") },
    { name: problem.shortName, path: lp(`/problemas/${problem.slug}/`) },
  ];

  return (
    <>
      <SEO
        title={problem.seoTitle}
        description={problem.metaDescription}
        path={`/problemas/${problem.slug}/`}
        image={problem.ogImage ? localeUrl("es", problem.ogImage) : undefined}
        jsonLd={[breadcrumbSchema(crumbs)]}
      />
      <Breadcrumbs items={crumbs} />
      <article className="container-site pb-8" data-testid={`problem-page-${problem.slug}`}>
        <header className="pt-10 max-w-3xl">
          <p className="tech-label mb-4">{c.kicker}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">{problem.h1}</h1>
        </header>
        <PagePhoto photo={pagePhotos[problem.slug]} className="mt-10 max-w-3xl" />

        <div className="max-w-3xl">
          <Reveal className="mt-10 bg-cadastral/5 border-l-2 border-cadastral p-6 sm:p-8 rounded-sm">
            <h2 className="text-lg font-semibold text-ink mb-3">{c.answer}</h2>
            <p className="text-[15px] sm:text-base text-slate-700 leading-relaxed">{problem.answer}</p>
          </Reveal>

          <Reveal className="mt-12">
            <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-5">{c.causes}</h2>
            <ul className="space-y-3">
              {problem.causes.map((cause, i) => (
                <li key={i} className="flex gap-3 text-[15px] text-slate-700 leading-relaxed">
                  <span className="font-mono text-xs text-terracotta mt-1 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span>{cause}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="mt-12">
            <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-5">{c.checks}</h2>
            <ul className="space-y-3">
              {problem.checks.map((check, i) => (
                <li key={i} className="flex gap-3 text-[15px] text-slate-700 leading-relaxed">
                  <AlertCircle size={17} className="mt-1 shrink-0 text-cadastral" aria-hidden="true" />
                  <span>{check}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal className="mt-12">
            <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-4">{c.pros}</h2>
            <p className="text-[15px] sm:text-base text-slate-700 leading-relaxed">{problem.professional}</p>
          </Reveal>

          <Reveal className="mt-12 bg-paper-tint border hairline rounded-sm p-6 sm:p-8">
            <h2 className="text-lg font-semibold text-ink mb-2">{c.serviceH}</h2>
            <p className="text-sm text-slate-600 mb-5">{c.serviceNote}</p>
            <ul className="space-y-3">
              {problem.services.map((s) => {
                const rel = services.find((x) => x.slug === s);
                return rel ? (
                  <li key={s}>
                    <Link to={lp(`/servicios/${s}/`)} className="group flex items-center justify-between text-[15px] font-medium text-ink hover:text-terracotta transition-colors">
                      {rel.name}
                      <ArrowRight size={15} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" aria-hidden="true" />
                    </Link>
                  </li>
                ) : null;
              })}
            </ul>
            <Link
              to={lp(`/contacto/?necesidad=${encodeURIComponent(cm.unclearNeed)}`)}
              data-testid="problem-cta"
              onClick={() => trackEvent("cta_budget_click", { location: `problem:${problem.slug}` })}
              className="btn-primary mt-7"
            >
              {cm.tellCase} <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </Reveal>

          {problem.relatedGuides.length > 0 && (
            <Reveal className="mt-10">
              <h2 className="tech-label mb-4">{cm.relatedGuides}</h2>
              <ul className="space-y-2.5">
                {problem.relatedGuides.map((g) => {
                  const rel = guides.find((x) => x.slug === g);
                  return rel ? (
                    <li key={g}>
                      <Link to={lp(`/guias/${g}/`)} className="link-underline text-[15px]">{rel.title}</Link>
                    </li>
                  ) : null;
                })}
              </ul>
            </Reveal>
          )}
        </div>
      </article>
      <CTASection />
    </>
  );
}
